/**
 * GDI Link Resolver
 *
 * Resolves a GDILinkBlock's fields into a usable href and link props.
 * Handles product links, category links, page links, and external links.
 *
 * LinkType values:
 *   'Product ID'         – single product page  (LinkReference = product ID)
 *   'Category ID'        – category page         (LinkReference = category ID)
 *   'CMS Page ID'        – CMS-authored page     (LinkReference = page path)
 *   'Internal Link'      – storefront route      (LinkReference = route slug, e.g. "login")
 *   'External Link'      – absolute URL          (LinkReference = full URL)
 *   'Popover Product IDs'– multi-product popover (handled by gdiCtaBlock, not a link)
 *   'System Drawer ID'   – system drawer trigger (handled by gdiCtaBlock, not a link)
 *   'Custom Drawer'      – custom drawer trigger (handled by gdiCtaBlock, not a link)
 */

/**
 * Canonical GDILinkBlock `LinkType` values from CMS. Use these instead of raw
 * strings so a future CMS rename only needs to change one place.
 */
export const GDI_LINK_TYPES = Object.freeze({
    PRODUCT_ID: 'Product ID',
    CATEGORY_ID: 'Category ID',
    CMS_PAGE_ID: 'CMS Page ID',
    INTERNAL: 'Internal Link',
    EXTERNAL: 'External Link',
    POPOVER_PRODUCT_IDS: 'Popover Product IDs',
    SYSTEM_DRAWER_ID: 'System Drawer ID',
    CUSTOM_DRAWER: 'Custom Drawer'
})

/** Every supported GDILinkBlock LinkType — useful for membership checks. */
export const ALL_GDI_LINK_TYPES = Object.freeze(Object.values(GDI_LINK_TYPES))

/** Prefixes a storefront path with the current site/locale (e.g. /ca, /fr-ca, /us). */
const localizeInternalPath = (path, getLinkData) =>
    getLinkData?.({__typename: 'InternalPathLink', Path: path})?.generatedURL || path

/**
 * Resolve a GDILinkBlock into link props.
 *
 * @param {Object} linkBlock - The expanded GDILinkBlock content
 * @param {Function} getLinkData - From useGetLinkData() context (resolves category/product URLs)
 * @returns {Object|null} - { href, isExternal, ariaLabel, target, rel } or null
 */
export const resolveGDILink = (linkBlock, getLinkData) => {
    if (!linkBlock) return null

    const {LinkType, LinkReference, AriaLabel, OpenInNewTab, resolvedUrl} = linkBlock

    if (!LinkType || !LinkReference) return null

    let href = null
    let isExternal = false

    switch (LinkType) {
        case GDI_LINK_TYPES.EXTERNAL:
            /* CMS authors may type a URL without a protocol (e.g. "www.google.com").
             * Browsers would then treat it as a relative path. Normalize by adding
             * "https://" when the value isn't already absolute or protocol-relative. */
            href = /^([a-z][a-z0-9+.-]*:)?\/\//i.test(LinkReference)
                ? LinkReference
                : `https://${LinkReference}`
            isExternal = true
            break

        case GDI_LINK_TYPES.PRODUCT_ID:
            /* SSR injects `resolvedUrl` onto GDILinkBlock entries with a Product ID
             * LinkType (see resolveProductLinkUrls in ssr.js). */
            if (getLinkData) {
                const linkData = getLinkData({
                    __typename: 'ProductLinkBlock',
                    ProductId: LinkReference,
                    resolvedUrl
                })
                href = linkData?.generatedURL || resolvedUrl || `/product/${LinkReference}`
            } else {
                href = resolvedUrl || `/product/${LinkReference}`
            }
            break

        case GDI_LINK_TYPES.CATEGORY_ID:
            /* SSR injects `resolvedUrl` onto GDILinkBlock entries with a Category ID
             * LinkType (see resolveCategoryLinkUrls in ssr.js). */
            if (getLinkData) {
                const linkData = getLinkData({
                    __typename: 'CategoryLinkBlock',
                    CategoryId: LinkReference,
                    resolvedUrl
                })
                href = linkData?.generatedURL || resolvedUrl || `/category/${LinkReference}`
            } else {
                href = resolvedUrl || `/category/${LinkReference}`
            }
            break

        case GDI_LINK_TYPES.CMS_PAGE_ID:
            href = localizeInternalPath(`/${LinkReference}.html`, getLinkData)
            break

        case GDI_LINK_TYPES.INTERNAL:
            href = localizeInternalPath(
                LinkReference.startsWith('/') ? LinkReference : `/${LinkReference}`,
                getLinkData
            )
            break

        case GDI_LINK_TYPES.POPOVER_PRODUCT_IDS:
        case GDI_LINK_TYPES.SYSTEM_DRAWER_ID:
        case GDI_LINK_TYPES.CUSTOM_DRAWER:
            // These are handled by the consuming component (e.g. gdiCtaBlock);
            // they do not resolve to a navigable href.
            return null

        default:
            return null
    }

    if (!href) return null

    return {
        href,
        isExternal,
        ariaLabel: AriaLabel || undefined,
        target: OpenInNewTab ? '_blank' : undefined,
        rel: OpenInNewTab ? 'noopener noreferrer' : undefined
    }
}

/**
 * Whether GDILinkBlock can render a clickable element for the given linkBlock.
 * Returns true for navigable types (resolveGDILink succeeds) and for the two
 * interactive types GDILinkBlock handles directly (Custom Drawer with a content
 * area, System Drawer ID with a reference). Consumers can use this to decide
 * between wrapping content in <GDILinkBlock> versus applying a fallback (e.g.
 * routing to a referenced category, opening a next-level popover).
 *
 * @param {Object|null} linkBlock - The expanded GDILinkBlock content
 * @param {Function} [getLinkData] - From useGetLinkData() context (optional)
 * @returns {boolean}
 */
export const isGDILinkUsable = (linkBlock, getLinkData) => {
    if (!linkBlock) return false
    if (linkBlock.LinkType === GDI_LINK_TYPES.SYSTEM_DRAWER_ID && linkBlock.LinkReference)
        return true
    if (linkBlock.LinkType === GDI_LINK_TYPES.CUSTOM_DRAWER && linkBlock.CustomDrawer) return true
    if (
        linkBlock.LinkType === GDI_LINK_TYPES.POPOVER_PRODUCT_IDS &&
        typeof linkBlock.LinkReference === 'string' &&
        linkBlock.LinkReference.split(',').some((id) => id.trim())
    ) {
        return true
    }
    return Boolean(resolveGDILink(linkBlock, getLinkData)?.href)
}
